import React, { useState, useEffect } from 'react';
import { collection, query, getDocs, doc, updateDoc, deleteField, where } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { trackError, createErrorMeta } from '../../lib/telemetry';

interface FlaggedUser {
  uid: string;
  email: string;
  name: string;
  flaggedAt: any;
  reason: string;
  dateKey: string;
  uploadStats: {
    bytesUploaded: number;
    bytesLimit: number;
    bytesExceeded: number;
    countUploaded: number;
    countLimit: number;
    countExceeded: number;
    lastFileName: string;
    lastFileSize: number;
  };
  billing: {
    plan: string;
    status: string;
  };
  status: 'pending_review' | 'cleared' | 'confirmed_abuse';
  reviewedAt: any;
  reviewedBy: string | null;
  notes: string | null;
}

const AbuseQueue: React.FC = () => {
  const [flaggedUsers, setFlaggedUsers] = useState<FlaggedUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  useEffect(() => {
    fetchFlaggedUsers();
  }, []);

  const fetchFlaggedUsers = async () => {
    try {
      setLoading(true);

      // Query _flagged_users collection for pending reviews
      const flaggedRef = collection(db, '_flagged_users');
      const pendingQuery = query(flaggedRef, where('status', '==', 'pending_review'));
      const snapshot = await getDocs(pendingQuery);

      const users: FlaggedUser[] = [];
      snapshot.forEach(docSnap => {
        users.push(docSnap.data() as FlaggedUser);
      });

      // Sort by flagged date (newest first)
      users.sort((a, b) => {
        const aTime = a.flaggedAt?.toMillis?.() || 0;
        const bTime = b.flaggedAt?.toMillis?.() || 0;
        return bTime - aTime;
      });

      setFlaggedUsers(users);
    } catch (error) {
      trackError(
        'Failed to fetch abuse queue',
        createErrorMeta('security', 'SEC_RULE_DENIED_WRITE', 'S3', 'degraded', '24h', {
          userAction: 'admin_abuse_queue_fetch',
        }),
        error
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClearHold = async (userId: string) => {
    try {
      setActionLoading(userId);

      // Clear the user's hold flag
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        'flags.onHold': deleteField(),
        'flags.reason': deleteField(),
        'flags.at': deleteField(),
        'flags.threshold': deleteField(),
      });

      // Update the _flagged_users entry
      const flaggedRef = doc(db, '_flagged_users', userId);
      await updateDoc(flaggedRef, {
        status: 'cleared',
        reviewedAt: new Date(),
        notes: 'Cleared by admin - false positive',
      });

      // Refresh the list
      await fetchFlaggedUsers();
    } catch (error) {
      trackError(
        'Failed to clear hold',
        createErrorMeta('security', 'SEC_RULE_DENIED_WRITE', 'S2', 'user-blocking', 'immediate', {
          userAction: 'admin_clear_hold',
        }),
        error
      );
    } finally {
      setActionLoading(null);
    }
  };

  const handleConfirmAbuse = async (userId: string) => {
    try {
      setActionLoading(userId);

      // Keep the hold and mark as confirmed abuse
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        'flags.reason': 'manual_review',
        'flags.confirmedAt': new Date(),
      });

      // Update the _flagged_users entry
      const flaggedRef = doc(db, '_flagged_users', userId);
      await updateDoc(flaggedRef, {
        status: 'confirmed_abuse',
        reviewedAt: new Date(),
        notes: 'Confirmed as abuse by admin',
      });

      // TODO: Send canned email to user

      // Refresh the list
      await fetchFlaggedUsers();
    } catch (error) {
      trackError(
        'Failed to confirm abuse',
        createErrorMeta('security', 'SEC_RULE_DENIED_WRITE', 'S2', 'user-blocking', 'immediate', {
          userAction: 'admin_confirm_abuse',
        }),
        error
      );
    } finally {
      setActionLoading(null);
    }
  };

  const formatBytes = (bytes: number) => {
    if (bytes < 1024 * 1024) {
      return `${Math.round(bytes / 1024)} KB`;
    } else if (bytes < 1024 * 1024 * 1024) {
      return `${Math.round(bytes / (1024 * 1024))} MB`;
    } else {
      return `${Math.round(bytes / (1024 * 1024 * 1024))} GB`;
    }
  };

  const formatDate = (timestamp?: any) => {
    if (!timestamp) return 'Unknown';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-gray-900">Abuse Queue</h1>
          <p className="mt-2 text-sm text-gray-700">
            Review accounts flagged for excessive upload activity
          </p>
        </div>
      </div>

      <div className="mt-8 flex flex-col">
        <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
              {flaggedUsers.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-500">No accounts currently on hold</p>
                </div>
              ) : (
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th
                        scope="col"
                        className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                      >
                        User
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        Reason
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        Threshold
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        Flagged At
                      </th>
                      <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {flaggedUsers.map(user => (
                      <tr key={user.uid}>
                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm sm:pl-6">
                          <div className="ml-4">
                            <div className="font-medium text-gray-900">{user.name}</div>
                            <div className="text-gray-500">{user.email}</div>
                            <div className="text-gray-400 text-xs">{user.uid}</div>
                            <div className="text-xs text-blue-600 mt-1">
                              Plan: {user.billing.plan} ({user.billing.status})
                            </div>
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          <span
                            className={`inline-flex rounded-full px-2 text-xs font-semibold leading-5 ${
                              user.reason === 'excessive_upload'
                                ? 'bg-red-100 text-red-800'
                                : 'bg-yellow-100 text-yellow-800'
                            }`}
                          >
                            {user.reason === 'excessive_upload'
                              ? 'Excessive Upload'
                              : 'Manual Review'}
                          </span>
                        </td>
                        <td className="px-3 py-4 text-sm text-gray-500">
                          <div className="space-y-1">
                            <div className="font-semibold text-red-600">
                              {formatBytes(user.uploadStats.bytesUploaded)} /{' '}
                              {formatBytes(user.uploadStats.bytesLimit)}
                            </div>
                            <div className="text-xs text-red-500">
                              Exceeded by: {formatBytes(user.uploadStats.bytesExceeded)}
                            </div>
                            <div className="text-xs text-gray-600">
                              {user.uploadStats.countUploaded} files (limit:{' '}
                              {user.uploadStats.countLimit})
                            </div>
                            <div className="text-xs text-gray-400 mt-1">
                              Last: {user.uploadStats.lastFileName}
                            </div>
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {formatDate(user.flaggedAt)}
                          <div className="text-xs text-gray-400">Date: {user.dateKey}</div>
                        </td>
                        <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                          <button
                            onClick={() => handleClearHold(user.uid)}
                            disabled={actionLoading === user.uid}
                            className="text-green-600 hover:text-green-900 mr-4 disabled:opacity-50"
                          >
                            {actionLoading === user.uid ? 'Processing...' : 'Clear Hold'}
                          </button>
                          <button
                            onClick={() => handleConfirmAbuse(user.uid)}
                            disabled={actionLoading === user.uid}
                            className="text-red-600 hover:text-red-900 disabled:opacity-50"
                          >
                            {actionLoading === user.uid ? 'Processing...' : 'Confirm Abuse'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AbuseQueue;
